import { AlertCircle, Bike, Calendar, Clock, Receipt, Trash2, Truck } from "lucide-react";
import { useBooking } from "../contexts/BookingContext";
import { useTranslation } from "../hooks/useTranslation";
import {
  calculateDeliveryCost,
  calculateDurationInDays,
  calculateDynamicPrice,
  getPriceDescription,
} from "../services/pricingService";
import "../styles/price-breakdown-light.css";
import { formatPriceWithEuro } from "../utils/priceFormatter";

const PriceBreakdown = ({
  selectedVehicles,
  selectedDates,
  deliveryType = "pickup",
  selectedAccessories = {},
  accessoriesByModel = {},
  pricing = null,
  showInsuranceWarnings = false,
  showInseamStep5Hint = false,
}) => {
  const { t } = useTranslation();
  const { updatePricingWithSeasons, bookingData } = useBooking();

  const handleRemoveVehicle = (vehicleIdToRemove) => {
    if (!vehicleIdToRemove) return;
    const nextVehicles = (selectedVehicles || []).filter(
      (v) => v && v.id !== vehicleIdToRemove
    );
    const nextAccessories = { ...(selectedAccessories || {}) };
    delete nextAccessories[vehicleIdToRemove];
    updatePricingWithSeasons({
      selectedVehicles: nextVehicles,
      selectedAccessories: nextAccessories,
    });
  };

  const hasBackendTotalsForCurrentDates = () => {
    if (!selectedDates?.start || !selectedDates?.end) return false;
    if (!Array.isArray(selectedVehicles) || selectedVehicles.length === 0) return false;
    return selectedVehicles.every((v) => {
      if (!v) return false;
      const hasTotal =
        typeof v.totalPrice === "number" && Number.isFinite(v.totalPrice);
      if (!hasTotal) return false;
      // Si guardamos para qué fechas se calculó, exigir match exacto
      if (v.pricingStart != null && v.pricingEnd != null) {
        return (
          v.pricingStart === selectedDates.start &&
          v.pricingEnd === selectedDates.end
        );
      }
      return true;
    });
  };

  const availabilityIssues = bookingData?.availabilityIssues;
  const showAvailabilityIssue =
    availabilityIssues &&
    availabilityIssues.start === selectedDates?.start &&
    availabilityIssues.end === selectedDates?.end &&
    Array.isArray(availabilityIssues.removed) &&
    availabilityIssues.removed.length > 0;

  // Función para obtener el precio de un vehículo desde el contexto
  const getVehiclePriceFromContext = (vehicle) => {
    // Preferir siempre el totalPrice del vehículo si existe (viene de availability y ya incluye temporadas)
    if (
      typeof vehicle?.totalPrice === "number" &&
      Number.isFinite(vehicle.totalPrice)
    ) {
      return vehicle.totalPrice;
    }
    if (pricing?.vehicles?.items) {
      const vehiclePricing = pricing.vehicles.items.find(item => item.id === vehicle.id);
      if (vehiclePricing?.totalPrice) {
        return vehiclePricing.totalPrice;
      }
    }
    return null;
  };
  const calculateTotal = () => {
    // Si tenemos totales por vehículo válidos para estas fechas, recomponer el total desde ahí
    // para evitar divergencias con `pricing` recalculado por otros endpoints.
    if (hasBackendTotalsForCurrentDates()) {
      const vehiclesTotal = calculateVehiclesTotal();
      const accessoriesTotal = calculateAccessoriesTotal();
      const deliveryCost = calculateDeliveryCost(deliveryType);
      return Math.round((vehiclesTotal + accessoriesTotal + deliveryCost) * 100) / 100;
    }

    // Si hay pricing calculado del contexto, usar el grandTotal
    if (pricing?.grandTotal && typeof pricing.grandTotal === 'number') {
      return pricing.grandTotal;
    }

    // Si hay vehículos seleccionados, usar el totalPrice del backend directamente
    if (selectedVehicles.length > 0) {
      const vehiclesTotal = calculateVehiclesTotal();
      const accessoriesTotal = calculateAccessoriesTotal();
      const deliveryCost = calculateDeliveryCost(deliveryType);

      // Redondear a 2 decimales para evitar errores de precisión
      return Math.round((vehiclesTotal + accessoriesTotal + deliveryCost) * 100) / 100;
    }

    // Fallback al cálculo local si no hay pricing del contexto
    if (!selectedDates?.start || !selectedDates?.end) return 0;

    const vehiclesTotal = calculateVehiclesTotal();
    const accessoriesTotal = calculateAccessoriesTotal();
    const deliveryCost = calculateDeliveryCost(deliveryType);

    return Math.round((vehiclesTotal + accessoriesTotal + deliveryCost) * 100) / 100;
  };

  const calculateVehiclesTotal = () => {
    // Si los vehículos ya traen totalPrice para estas fechas, sumar eso y evitar el pricing del contexto
    if (hasBackendTotalsForCurrentDates()) {
      return selectedVehicles.reduce((s, v) => s + (Number(v?.totalPrice) || 0), 0);
    }
    // Si hay pricing calculado del contexto, usar ese total
    if (pricing?.vehicles?.total && typeof pricing.vehicles.total === 'number') {
      return pricing.vehicles.total;
    }

    // Usar los precios ya calculados del backend que vienen en selectedVehicles
    if (!selectedDates?.start || !selectedDates?.end) return 0;

    const durationInDays = calculateDurationInDays(
      selectedDates.start,
      selectedDates.end
    );

    // Usar los precios totales que ya vienen calculados del backend
    return selectedVehicles.reduce((total, vehicle) => {
      // Prioridad 1: Usar totalPrice del backend (precio calculado con temporadas)
      if (vehicle.totalPrice && typeof vehicle.totalPrice === 'number') {
        return total + vehicle.totalPrice;
      }
      
      // Prioridad 2: Fallback usando pricePerDay del backend
      if (vehicle.pricePerDay && typeof vehicle.pricePerDay === 'number') {
        const calculated = vehicle.pricePerDay * durationInDays;
        return total + calculated;
      }
      
      // Prioridad 3: Fallback usando basePrice del backend
      if (vehicle.basePrice && typeof vehicle.basePrice === 'number') {
        const calculated = vehicle.basePrice * durationInDays;
        return total + calculated;
      }
      
      // Último recurso: usar precio base del modelo
      if (vehicle.basePriceModel && typeof vehicle.basePriceModel === 'number') {
        const calculated = vehicle.basePriceModel * durationInDays;
        return total + calculated;
      }
      
      return total;
    }, 0);
  };

  const calculateAccessoriesTotal = () => {
    // Si hay pricing calculado del contexto, usar ese total
    if (pricing?.accessories?.total && typeof pricing.accessories.total === 'number') {
      return pricing.accessories.total;
    }

    // Fallback al cálculo local si no hay pricing del contexto
    if (!selectedDates?.start || !selectedDates?.end) return 0;

    const durationInDays = calculateDurationInDays(
      selectedDates.start,
      selectedDates.end
    );

    let total = 0;

    // Procesar accesorios seleccionados por vehículo
    for (const [vehicleId, selectedAccessoryIds] of Object.entries(selectedAccessories)) {
      if (Array.isArray(selectedAccessoryIds)) {
        // Encontrar el modelo del vehículo
        const vehicle = selectedVehicles.find(v => v.id == vehicleId);
        if (vehicle && vehicle.modelId) {
          // Obtener los accesorios disponibles para este modelo
          const modelAccessories = accessoriesByModel[vehicle.modelId] || [];
          
          // Calcular el total para los accesorios seleccionados de este vehículo
          const vehicleAccessoriesTotal = selectedAccessoryIds.reduce((vehicleTotal, accessoryId) => {
            const accessory = modelAccessories.find(acc => acc.id == accessoryId);
            if (accessory) {
              if (accessory.accessoryPriceType === 'FIXED') {
                return vehicleTotal + accessory.price;
              } else {
                // DAILY price
                return vehicleTotal + (accessory.price * durationInDays);
              }
            }
            return vehicleTotal;
          }, 0);
          
          total += vehicleAccessoriesTotal;
        }
      }
    }

    return total;
  };

  const calculateInsuranceTotal = () => {
    if (pricing?.accessories?.insuranceTotal != null && typeof pricing.accessories.insuranceTotal === 'number') {
      return pricing.accessories.insuranceTotal;
    }
    if (!selectedDates?.start || !selectedDates?.end) return 0;
    const durationInDays = calculateDurationInDays(selectedDates.start, selectedDates.end);
    let total = 0;
    for (const [vehicleId, selectedAccessoryIds] of Object.entries(selectedAccessories)) {
      if (!Array.isArray(selectedAccessoryIds)) continue;
      const vehicle = selectedVehicles.find(v => v.id == vehicleId);
      if (!vehicle?.modelId) continue;
      const modelAccessories = accessoriesByModel[vehicle.modelId] || [];
      selectedAccessoryIds.forEach((accessoryId) => {
        const accessory = modelAccessories.find(acc => acc.id == accessoryId);
        if (accessory?.category === 'INSURANCE') {
          const price = accessory.accessoryPriceType === 'FIXED' ? accessory.price : accessory.price * durationInDays;
          total += price;
        }
      });
    }
    return Math.round(total * 100) / 100;
  };

  const calculateDuration = () => {
    if (!selectedDates?.start || !selectedDates?.end) {
      return { days: 0 };
    }

    const start = new Date(selectedDates.start);
    const end = new Date(selectedDates.end);
    
    // Calcular días completos entre las fechas (inclusive)
    const timeDiff = end.getTime() - start.getTime();
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1; // +1 para incluir ambos días

    return {
      days: Math.max(1, days), // Mínimo 1 día
    };
  };

  const total = calculateTotal();
  const vehiclesTotal = calculateVehiclesTotal();
  const accessoriesTotal = calculateAccessoriesTotal();
  const insuranceTotal = calculateInsuranceTotal();
  const accessoriesOnlyTotal = accessoriesTotal - insuranceTotal;
  const duration = calculateDuration();

  const discountPercents3to5 = (() => {
    const set = new Set();
    for (const v of selectedVehicles) {
      const p = Number(v.threeToFive);
      if (Number.isFinite(p) && p > 0) set.add(p);
    }
    return [...set].sort((a, b) => a - b);
  })();
  const discountFromSeasonalItems =
    Array.isArray(pricing?.vehicles?.items) && pricing.vehicles.items.length > 0
      ? pricing.vehicles.items.reduce((s, it) => s + (Number(it.discountApplied) || 0), 0)
      : 0;
  const show3to5DiscountNote =
    vehiclesTotal > 0 &&
    duration.days >= 3 &&
    duration.days <= 5 &&
    (discountPercents3to5.length > 0 || discountFromSeasonalItems > 0.02);

  // Usar el coste de entrega del contexto si está disponible, sino calcularlo
  const deliveryCost = pricing?.delivery?.cost !== undefined 
    ? pricing.delivery.cost 
    : calculateDeliveryCost(deliveryType);

  // Contar bicicletas con al menos un seguro (categoría INSURANCE) seleccionado
  const vehiclesWithInsurance = selectedVehicles.filter((vehicle) => {
    const vehicleAccessoryIds = selectedAccessories[vehicle.id] || [];
    if (vehicleAccessoryIds.length === 0) return false;
    const modelAccessories = accessoriesByModel[vehicle.modelId] || [];
    return vehicleAccessoryIds.some(
      (accId) => modelAccessories.find((a) => a.id == accId)?.category === "INSURANCE"
    );
  }).length;
  const totalVehicles = selectedVehicles.length;
  const showNoInsuranceWarning = totalVehicles >= 1 && vehiclesWithInsurance === 0;
  const showPartialInsuranceWarning =
    totalVehicles > 1 && vehiclesWithInsurance > 0 && vehiclesWithInsurance < totalVehicles;
  const hasInseamEligibleVehicles = selectedVehicles.some((v) => v?.inseamOptionalEnabled);
  const showInseamHintBanner = showInseamStep5Hint && hasInseamEligibleVehicles;

  if (!selectedVehicles.length) {
    return (
      <div className="price-breakdown-light bg-background border border-primary rounded-xl p-6">
        <div className="flex gap-2 items-center mb-3">
          <Receipt size={18} className="text-primary" />
          <h3 className="font-semibold text-sm text-2c3e50">
            {t('price.title')}
          </h3>
        </div>
        <p className="text-6c757d text-center py-6 text-sm">
          {t('price.select.bikes')}
        </p>
      </div>
    );
  }

  return (
    <div className="price-breakdown-light bg-background border border-primary rounded-xl p-6">
      <div className="flex gap-2 items-center mb-3">
        <Receipt size={18} className="text-primary" />
        <h3 className="font-semibold text-sm text-2c3e50">
          {t('price.title')}
        </h3>
      </div>

      {/* Fechas seleccionadas */}
      <div className="mb-3">
        <div className="flex gap-2 items-center mb-1">
          <Calendar size={14} className="text-6c757d" />
          <span className="font-medium text-xs text-2c3e50">
            {t('price.selected.dates')}
          </span>
        </div>
        <p className="text-xs mb-1 text-6c757d">
          {new Date(selectedDates.start).toLocaleDateString()} -{" "}
          {new Date(selectedDates.end).toLocaleDateString()}
        </p>
        <div className="flex gap-2 items-center">
          <Clock size={12} className="text-6c757d" />
          <span className="text-xs text-6c757d">
            {t('price.duration')} {duration.days} {duration.days === 1 ? t('date.day') : t('date.days')}
          </span>
        </div>
      </div>

      <hr className="my-3 border-6c757d/30" />

      {/* Bicicletas seleccionadas */}
      <div className="mb-3">
        <div className="flex gap-2 items-center mb-2">
          <Bike size={14} className="text-6c757d" />
          <span className="font-medium text-xs text-2c3e50">
            {t('price.selected.bikes')}
          </span>
        </div>
        {showInseamHintBanner && (
          <div className="mb-2 p-2 rounded-lg border border-sky-500/30 bg-sky-500/10 text-sky-950 text-xs">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span className="font-medium">{t('inseam.step2.hint')}</span>
            </div>
          </div>
        )}
        {showAvailabilityIssue && (
          <div className="mb-2 p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-900 text-xs">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">
                  {t('bike.no.availability')}
                </div>
                <ul className="mt-1 list-disc pl-5">
                  {availabilityIssues.removed.slice(0, 3).map((r) => (
                    <li key={r.id}>
                      {r.model} ({r.size}) {r.serialNumber ? `- ${r.serialNumber}` : ""}
                    </li>
                  ))}
                  {availabilityIssues.removed.length > 3 && (
                    <li>y {availabilityIssues.removed.length - 3} {t('bike.more')}…</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="max-h-32 overflow-y-auto">
          {selectedVehicles.map((vehicle, index) => (
            <div
              key={index}
              className="mb-2 p-2 bg-background border border-EBC122/30 rounded-lg text-xs"
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="font-medium text-2c3e50 truncate">
                    {vehicle.model}
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center p-1 rounded-md text-6c757d hover:text-red-600 hover:bg-red-600/10 transition-colors"
                    onClick={() => handleRemoveVehicle(vehicle.id)}
                    aria-label="Eliminar bicicleta"
                    title="Eliminar bicicleta"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <span className="font-semibold text-primary">
                  {getPriceDescription(
                    vehicle,
                    duration.days,
                    t('date.day')
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-6c757d">
                  {t('price.size')} {vehicle.size} - {t('price.number')} {vehicle.serialNumber}
                </span>
                <span className="text-6c757d font-medium">
                  {formatPriceWithEuro(
                    (() => {
                      const contextPrice = getVehiclePriceFromContext(vehicle);
                      if (typeof contextPrice === "number" && Number.isFinite(contextPrice)) {
                        return contextPrice;
                      }
                      
                      // Usar los precios ya calculados del backend
                      if (vehicle.totalPrice && typeof vehicle.totalPrice === 'number') {
                        return vehicle.totalPrice;
                      }
                      
                      // Fallback: usar el precio por día del backend multiplicado por la duración
                      if (vehicle.pricePerDay && typeof vehicle.pricePerDay === 'number') {
                        const calculated = vehicle.pricePerDay * duration.days;
                        return Math.round(calculated * 100) / 100;
                      }
                      
                      // Último recurso: replicar el cálculo estándar con los campos del vehículo
                      // (basePrice/threeToFive/additionalDay). Así card y desglose quedan alineados.
                      const calculated = calculateDynamicPrice(vehicle, duration.days);
                      return Math.round(calculated * 100) / 100;
                    })()
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Accesorios seleccionados */}
      {accessoriesTotal > 0 && (
        <div className="mb-3">
          <hr className="my-3 border-6c757d/30" />
          <div className="flex gap-2 items-center mb-2">
            <span className="font-medium text-xs text-2c3e50">
              {t('price.accessories.selected')}
            </span>
          </div>
          <div className="max-h-32 overflow-y-auto">
            {Object.entries(selectedAccessories || {}).map(([vehicleId, selectedAccessoryIds]) => {
              if (!Array.isArray(selectedAccessoryIds) || selectedAccessoryIds.length === 0) {
                return null;
              }

              // Encontrar el vehículo
              const vehicle = selectedVehicles.find(v => v.id == vehicleId);
              if (!vehicle || !vehicle.modelId) {
                return null;
              }

              // Obtener los accesorios del modelo
              const modelAccessories = accessoriesByModel[vehicle.modelId] || [];

              return selectedAccessoryIds.map((accessoryId) => {
                const accessory = modelAccessories.find(acc => acc.id == accessoryId);
                if (!accessory) return null;

                const totalPrice = accessory.accessoryPriceType === 'FIXED' 
                  ? accessory.price 
                  : accessory.price * duration.days;

                return (
                  <div
                    key={`${vehicleId}-${accessoryId}`}
                    className="mb-2 p-2 bg-background border border-EBC122/30 rounded-lg text-xs"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-2c3e50">
                        {accessory.name} ({vehicle.model})
                      </span>
                      <span className="font-semibold text-primary">
                        {formatPriceWithEuro(totalPrice)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-6c757d">
                        {accessory.accessoryPriceType === 'FIXED' 
                          ? formatPriceWithEuro(accessory.price) + ` (${t('price.accessory.fixed')})`
                          : formatPriceWithEuro(accessory.price) + `/${t('price.accessory.per.day')}`
                        }
                      </span>
                      {accessory.accessoryPriceType === 'DAILY' && (
                        <span className="text-6c757d font-medium">
                          {duration.days} {t('date.days')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              });
            })}
          </div>
        </div>
      )}

      <hr className="my-3 border-6c757d/30" />

      {/* Avisos de seguro en el desglose (solo desde el paso de accesorios, paso 3) */}
      {showInsuranceWarnings && showNoInsuranceWarning && (
        <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex gap-2 items-start text-amber-700">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span className="text-xs font-medium">{t("price.warning.insurance.none")}</span>
          </div>
        </div>
      )}
      {showInsuranceWarnings && showPartialInsuranceWarning && (
        <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex gap-2 items-start text-amber-700">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span className="text-xs font-medium">
              {vehiclesWithInsurance === 1
                ? t("price.warning.insurance.only.one.bike")
                : t("price.warning.insurance.partial", { count: vehiclesWithInsurance })}
            </span>
          </div>
        </div>
      )}

      {/* Desglose de precios (con base e IVA por línea cuando viene del servicio de precios) */}
      <div className="mb-3">
        {vehiclesTotal > 0 && (
          <div className="mb-1.5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-6c757d">
                {t('price.bikes')} ({duration.days} {t('date.days')})
              </span>
              <span className="font-medium text-sm text-2c3e50">
                {formatPriceWithEuro(pricing?.vehicles?.total ?? vehiclesTotal)}
              </span>
            </div>
            {/* {typeof pricing?.vehicles?.base === 'number' && typeof pricing?.vehicles?.vat === 'number' && (
              <div className="flex justify-end gap-3 mt-0.5 text-xs text-6c757d">
                <span>{t('price.base')}: {formatPriceWithEuro(pricing.vehicles.base)}</span>
                <span>{t('price.vat')}: {formatPriceWithEuro(pricing.vehicles.vat)}</span>
              </div>
            )} */}
            {show3to5DiscountNote && (
              <p className="mt-2 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-2.5 py-2 text-[11px] leading-snug text-emerald-950">
                {discountPercents3to5.length === 1
                  ? t('price.discount.3to5.info', { percent: discountPercents3to5[0] })
                  : discountPercents3to5.length > 1
                    ? t('price.discount.3to5.info.mixed')
                    : t('price.discount.3to5.info.generic')}
              </p>
            )}
          </div>
        )}
        {accessoriesOnlyTotal > 0 && (
          <div className="mb-1.5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-6c757d">
                {t('price.accessories')} ({duration.days} {t('date.days')})
              </span>
              <span className="font-medium text-sm text-2c3e50">
                {formatPriceWithEuro(pricing?.accessories?.accessoriesOnlyTotal ?? accessoriesOnlyTotal)}
              </span>
            </div>
            {/* {typeof pricing?.accessories?.accessoriesOnlyBase === 'number' && typeof pricing?.accessories?.accessoriesOnlyVat === 'number' && (
              <div className="flex justify-end gap-3 mt-0.5 text-xs text-6c757d">
                <span>{t('price.base')}: {formatPriceWithEuro(pricing.accessories.accessoriesOnlyBase)}</span>
                <span>{t('price.vat')}: {formatPriceWithEuro(pricing.accessories.accessoriesOnlyVat)}</span>
              </div>
            )} */}
          </div>
        )}
        {insuranceTotal > 0 && (
          <div className="mb-1.5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-6c757d">
                {t('payment.summary.insurance.total')}
              </span>
              <span className="font-medium text-sm text-2c3e50">
                {formatPriceWithEuro(pricing?.accessories?.insuranceTotal ?? insuranceTotal)}
              </span>
            </div>
            {/* {typeof pricing?.accessories?.insuranceBase === 'number' && typeof pricing?.accessories?.insuranceVat === 'number' && (
              <div className="flex justify-end gap-3 mt-0.5 text-xs text-6c757d">
                <span>{t('price.base')}: {formatPriceWithEuro(pricing.accessories.insuranceBase)}</span>
                <span>{t('price.vat')}: {formatPriceWithEuro(pricing.accessories.insuranceVat)}</span>
              </div>
            )} */}
          </div>
        )}
        {deliveryCost > 0 && (
          <div className="mb-1.5">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <Truck size={12} className="text-6c757d" />
                <span className="text-xs text-6c757d">{t('price.delivery.home')}</span>
              </div>
              <span className="font-medium text-sm text-2c3e50">
                {formatPriceWithEuro(pricing?.delivery?.cost ?? deliveryCost)}
              </span>
            </div>
            {/* {typeof pricing?.delivery?.base === 'number' && typeof pricing?.delivery?.vat === 'number' && (
              <div className="flex justify-end gap-3 mt-0.5 text-xs text-6c757d">
                <span>{t('price.base')}: {formatPriceWithEuro(pricing.delivery.base)}</span>
                <span>{t('price.vat')}: {formatPriceWithEuro(pricing.delivery.vat)}</span>
              </div>
            )} */}
          </div>
        )}
      </div>

      <hr className="my-3 border-6c757d/30" />

      {/* Total (actualizado con base + IVA por línea cuando viene del servicio de precios) */}
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <span className="text-base font-bold text-2c3e50">{t('price.grand.total')}</span>
          <span className="text-xs text-6c757d mt-1">{t('price.vat.included')}</span>
        </div>
        <span className="text-lg font-bold text-gray-500">
          {formatPriceWithEuro(pricing?.grandTotal != null ? pricing.grandTotal : total)}
        </span>
      </div>
    </div>
  );
};

export default PriceBreakdown;
